/**
 * The Installed tab: the packs this project runs, with their version, state, and pinned digest.
 *
 * The data is the same install list the Skills page reads, presented where a project goes to
 * choose a pack. Two pages showing the same records from the same endpoint is the point: a
 * person who installed something from the Marketplace should be able to see it there, not only
 * on a settings page they have to know about.
 *
 * What is deliberately absent: a revoked state. The install record has no `revoked` status and no
 * console route revokes a pack, so a column for it would be a promise the platform does not keep.
 */
import { useQuery } from "@tanstack/react-query";
import type {
  SkillInstall,
  SkillSource,
  SkillStatus,
  SkillUpdateState,
} from "@zworks/console-contracts";
import { EmptyState, ErrorState, ForbiddenState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { ApiError, skillsQuery } from "@/lib/api";

const updateLabels: Record<
  SkillUpdateState,
  { label: string; variant: "success" | "warning" | "info" }
> = {
  "up-to-date": { label: "Up to date", variant: "success" },
  "update-available": { label: "Update available", variant: "warning" },
  updating: { label: "Updating", variant: "info" },
};

const statusLabels: Record<SkillStatus, { label: string; variant: "info" | "warning" | "muted" }> =
  {
    enabled: { label: "Enabled", variant: "info" },
    trial: { label: "Trial", variant: "warning" },
    private: { label: "Private", variant: "muted" },
    // The platform withdrew this pack's digest. It stays in the list rather than vanishing, so
    // a person looking for what they installed is not left wondering where it went.
    revoked: { label: "Revoked", variant: "muted" },
  };

const sourceLabels: Record<SkillSource, string> = {
  marketplace: "Marketplace",
  uploaded: "Uploaded",
};

/** The state a record predating the field is treated as: enabled, and from the Marketplace. */
function updateOf(install: SkillInstall) {
  return updateLabels[install.updateState] ?? updateLabels["up-to-date"];
}

function statusOf(install: SkillInstall) {
  return install.status === undefined ? statusLabels.enabled : statusLabels[install.status];
}

export function InstalledPacksSection({ projectId }: { projectId: string }) {
  const installs = useQuery(skillsQuery(projectId));

  if (installs.isPending) {
    return <LoadingState label="Loading the packs this project runs…" />;
  }
  if (installs.isError) {
    const error = installs.error;
    if (error instanceof ApiError && error.status === 403) {
      return (
        <ForbiddenState title="Not available" description="You cannot read this project's packs." />
      );
    }
    return (
      <ErrorState
        title="The installed packs could not be loaded"
        requestId=""
        description={
          error instanceof ApiError
            ? (error.problem?.detail ?? error.message)
            : "The installed packs could not be loaded."
        }
        onRetry={() => void installs.refetch()}
      />
    );
  }

  const rows = installs.data ?? [];
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No packs installed yet"
        description="Install one from the Packs tab, or from the organisation library, and it appears here with its version and update state."
      />
    );
  }

  const revoked = rows.filter((row) => row.status === "revoked");

  return (
    <section aria-labelledby="installed-heading" className="flex flex-col gap-4">
      <h2 id="installed-heading" className="text-lg font-semibold">
        Installed
      </h2>
      {/* A withdrawal is the platform acting on the project, so it is said plainly at the top
          rather than left to a badge somebody may not scroll to. */}
      {revoked.length > 0 ? (
        <div
          role="alert"
          className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3"
        >
          <p className="text-sm font-medium">
            {revoked.length === 1
              ? "One pack this project runs has been revoked by the platform."
              : `${revoked.length} packs this project runs have been revoked by the platform.`}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            A revoked pack cannot start a new task. Remove it, or ask the publisher for a fixed
            version.
          </p>
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">
            The packs this project runs, with version, state, source, and pinned digest.
          </caption>
          <thead>
            <tr className="border-b text-left">
              <th scope="col" className="py-2 pr-4 font-medium">
                Pack
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                Version
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                State
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                Status
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                Source
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((install) => {
              const update = updateOf(install);
              const status = statusOf(install);
              return (
                <tr key={install.id} className="border-b last:border-b-0">
                  <td className="py-2 pr-4">
                    <span className="font-medium">{install.skill}</span>
                    {install.digest ? (
                      // The digest is what pins the content; a short prefix is enough to compare
                      // two rows by eye, and the full value is in the title.
                      <span
                        className="block font-mono text-xs text-muted-foreground"
                        title={install.digest}
                      >
                        {install.digest.slice(0, 19)}…
                      </span>
                    ) : null}
                  </td>
                  <td className="py-2 pr-4 tabular-nums">{install.version}</td>
                  <td className="py-2 pr-4">
                    <Badge variant={update.variant}>{update.label}</Badge>
                  </td>
                  <td className="py-2 pr-4">
                    <Badge variant={status.variant}>{status.label}</Badge>
                  </td>
                  <td className="py-2 pr-4 text-muted-foreground">
                    {install.source === undefined
                      ? sourceLabels.marketplace
                      : sourceLabels[install.source]}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
